// Shared convention for storing structured quick-tags inside the plain-text
// applications.employer_notes field, so tags can be rendered as pills
// separate from free-typed text instead of being mixed in as plain lines.
//
// Existing notes (saved before this convention existed) have no meta block,
// so they parse as { tags: [], text: <the raw note> } -- there's no reliable
// way to retroactively tell which of their lines came from a quick-tag click,
// so they just render as plain free text, unchanged.
const META_RE = /^\[\[NOTE_META:(.*?)\]\]\n?/s;

// `value` is what actually gets stored in a note's tags array (and round-tripped through
// applications.employer_notes via serializeEmployerNote/parseEmployerNote) -- kept as the
// original stable English literal so stored notes and tagSentiment's keyword matching below
// never depend on the viewer's display language. `labelKey` is resolved separately via t()
// wherever a tag is rendered, matching the value/label split used elsewhere in the app for
// this exact bug shape (regionOf(), SCHOOL_TYPE_KEYS, DeleteAccountModal's CANCEL_REASONS).
export const QUICK_NOTE_TAGS = [
  { value: 'Strong technical skills', labelKey: 'interview.noteTagStrongTechnical' },
  { value: 'Good communicator', labelKey: 'interview.noteTagGoodCommunicator' },
  { value: 'Needs more experience', labelKey: 'interview.noteTagNeedsExperience' },
];

const POSITIVE_KEYWORDS = ['strong', 'good', 'excellent', 'great', 'skilled', 'communicat'];
const NEGATIVE_KEYWORDS = ['needs', 'lack', 'weak', 'concern', 'poor', 'limited', 'issue'];

// Always called with the stored `value` (stable English), never a translated label -- see
// QUICK_NOTE_TAGS above.
export function tagSentiment(tag) {
  const lower = (tag || '').toLowerCase();
  if (NEGATIVE_KEYWORDS.some(k => lower.includes(k))) return 'negative';
  if (POSITIVE_KEYWORDS.some(k => lower.includes(k))) return 'positive';
  return 'neutral';
}

// Look up the labelKey for a stored tag value, for display -- falls back to the raw stored
// string for any tag that predates this convention or doesn't match a known quick-tag.
export function noteTagLabelKey(value) {
  return QUICK_NOTE_TAGS.find(t => t.value === value)?.labelKey || null;
}

// raw employer_notes string -> { tags, origin, addedAt, text, authorId, authorName }
// authorId/authorName identify whichever employer_staff member most recently saved this
// note -- there's only ever one note per candidate (a shared scratchpad, not a discrete
// list of entries), so "author" here means "last edited by", the same way addedAt already
// meant "last touched at" rather than "originally created at" before this field existed.
export function parseEmployerNote(raw) {
  if (!raw) return { tags: [], origin: null, addedAt: null, text: '', authorId: null, authorName: null };
  const match = raw.match(META_RE);
  if (!match) return { tags: [], origin: null, addedAt: null, text: raw, authorId: null, authorName: null };
  try {
    const meta = JSON.parse(match[1]);
    return {
      tags: Array.isArray(meta.tags) ? meta.tags : [],
      origin: meta.origin || null,
      addedAt: meta.addedAt || null,
      authorId: meta.authorId || null,
      authorName: meta.authorName || null,
      text: raw.slice(match[0].length),
    };
  } catch {
    return { tags: [], origin: null, addedAt: null, text: raw, authorId: null, authorName: null };
  }
}

// { tags, origin, addedAt, text, authorId, authorName } -> raw employer_notes string
export function serializeEmployerNote({ tags = [], origin = null, addedAt = null, text = '', authorId = null, authorName = null }) {
  if (tags.length === 0 && !origin && !authorId) return text;
  const meta = { tags };
  if (origin) meta.origin = origin;
  if (addedAt) meta.addedAt = addedAt;
  if (authorId) meta.authorId = authorId;
  if (authorName) meta.authorName = authorName;
  return `[[NOTE_META:${JSON.stringify(meta)}]]\n${text}`;
}
